import { createApi } from '@reduxjs/toolkit/query/react';

import { ResponseError } from '@/types/Error';
import {
  CupsPerDayPayload,
  CupsPerDayResponse,
  DeletePetProfilePayload,
  ManageBreedBreedType,
  PetBreedDetailsResponse,
  PetGroups,
  PetProfileCreateRequestPayload,
  PetProfileCreateResponsePayload,
  PetProfileResponse,
  RecommendedProductsPayload,
  RecommendedProductsType,
  Species,
  UpdatePetProfilePicturePayload,
  UpdatePetProfilePictureResponse,
} from '@/types/PetProfile';
import { SuccessResponse } from '@/types/Success';

import { generateBaseQuseryWithReAuth } from '../baseQueryReauth';

const contentTypes = {
  applicationJson: 'application/json',
  rawContentType: 'text/plain',
};

export const petProfileApi = createApi({
  reducerPath: 'petProfileApi',
  keepUnusedDataFor: 300,
  baseQuery: generateBaseQuseryWithReAuth({ basePath: 'Pet' }),
  tagTypes: [
    'UpdatePetProfileImage',
    'DeletePetProfile',
    'GetPetProfiles',
    'GetPetSpecies',
    'GetRecommendedProductsByPetProfileID',
    'GetCupsPerDayForAllPets',
  ],
  endpoints: builder => ({
    getPetProfiles: builder.mutation<SuccessResponse<PetProfileResponse>, void>({
      query: payload => ({
        url: `/GetPetProfiles`,
        method: 'GET',
        headers: {
          'Content-Type': contentTypes.rawContentType,
        },
      }),
      transformResponse: (response: SuccessResponse<PetProfileResponse>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    getRecommendedProducts: builder.mutation<SuccessResponse<RecommendedProductsType>, RecommendedProductsPayload>({
      query: payload => ({
        url: `/GetRecommendedProductsByPetProfileID?petprofileid=${payload.profileId}&customerid=${payload.customerId}`,
        method: 'GET',
        headers: {
          'Content-Type': contentTypes.rawContentType,
        },
      }),
      transformResponse: (response: SuccessResponse<RecommendedProductsType>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    updateProfile: builder.mutation<SuccessResponse<PetProfileCreateResponsePayload>, PetProfileCreateRequestPayload>({
      query: payload => ({
        url: `/EditProfile`,
        method: 'POST',
        body: payload,
        headers: {
          'Content-Type': contentTypes.applicationJson,
        },
      }),
      transformResponse: (response: SuccessResponse<PetProfileCreateResponsePayload>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    createPetProfile: builder.mutation<
      SuccessResponse<PetProfileCreateResponsePayload>,
      PetProfileCreateRequestPayload
    >({
      query: payload => ({
        url: `/createprofile`,
        method: 'POST',
        body: payload,
        headers: {
          'Content-Type': contentTypes.applicationJson,
        },
      }),
      transformResponse: (response: SuccessResponse<PetProfileCreateResponsePayload>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    updatePetProfile: builder.mutation<UpdatePetProfilePictureResponse, UpdatePetProfilePicturePayload>({
      query: payload => ({
        url: `/UpdatePetProfileImage?petprofileid=${payload.profileId}`,
        method: 'POST',
        body: JSON.stringify(payload.body),
        headers: {
          'Content-Type': contentTypes.applicationJson,
        },
      }),
      transformResponse: (response: UpdatePetProfilePictureResponse) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    deletePetProfile: builder.mutation<void, DeletePetProfilePayload>({
      query: payload => ({
        url: `/DeletePetProfile?petProfileId=${payload.petProfileId}`,
        method: 'POST',
      }),
      transformResponse: (response: void) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    getPetSpecies: builder.mutation<SuccessResponse<Species[]>, void>({
      query: payload => ({
        url: `/GetPetSpecies`,
        method: 'GET',
        headers: {
          'Content-Type': contentTypes.rawContentType,
        },
      }),
      transformResponse: (response: SuccessResponse<Species[]>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    createPetRecommendations: builder.mutation<
      SuccessResponse<PetProfileCreateResponsePayload>,
      PetProfileCreateRequestPayload
    >({
      query: payload => ({
        url: `/createprofile`,
        method: 'POST',
        body: payload,
        headers: {
          'Content-Type': contentTypes.applicationJson,
        },
      }),
      transformResponse: (response: SuccessResponse<PetProfileCreateResponsePayload>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    getCupsPerDay: builder.query<SuccessResponse<CupsPerDayResponse[]>, CupsPerDayPayload>({
      query: payload => ({
        url: payload.petOwnerId
          ? `/GetCupsPerDayForAllPets?customerId=${payload.petOwnerId}`
          : `/GetCupsPerDayForAllPets`,
        method: 'GET',
        headers: {
          'Content-Type': contentTypes.rawContentType,
        },
      }),
      transformResponse: (response: SuccessResponse<CupsPerDayResponse[]>) => response,
      transformErrorResponse: response => response as ResponseError,
      providesTags: ['GetCupsPerDayForAllPets'], // Add tags for cache invalidation if needed
    }),

    getPetBreedTypes: builder.query<SuccessResponse<ManageBreedBreedType[]>, number>({
      query: specie => ({
        url: `/GetPetBreedTypes?specie=${specie}`,
        method: 'GET',
        headers: {
          'Content-Type': contentTypes.rawContentType,
        },
      }),
      transformResponse: (response: SuccessResponse<ManageBreedBreedType[]>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),

    getPetBreedDetails: builder.query<SuccessResponse<PetBreedDetailsResponse[]>, void>({
      query: () => ({
        url: `/GetPetBreedDetails`,
        method: 'GET',
        headers: {
          'Content-Type': contentTypes.rawContentType,
        },
      }),
      transformResponse: (response: SuccessResponse<PetBreedDetailsResponse[]>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    addBreed: builder.mutation<
      SuccessResponse<null>,
      {
        petbreedname: string;
        petbreedtypeid: number;
      }
    >({
      query: payload => ({
        url: `/AddPetBreed`,
        method: 'POST',
        body: payload,
        headers: {
          'Content-Type': contentTypes.applicationJson,
        },
      }),
      transformResponse: (response: SuccessResponse<null>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),

    deleteBreed: builder.mutation<void, { petBreedId: number }>({
      query: ({ petBreedId }) => ({
        url: `/RemovePetBreed?petBreedId=${petBreedId}`,
        method: 'POST',
      }),
      transformResponse: (response: void) => response,
      transformErrorResponse: response => response as ResponseError,
    }),

    getPetGroupsByCustomerId: builder.query<SuccessResponse<PetGroups>, number>({
      query: customerId => ({
        url: `/GetPetProfileGroupByCustomerID?customerId=${customerId}`,
        method: 'GET',
        headers: {
          'Content-Type': contentTypes.rawContentType,
        },
      }),
      transformResponse: (response: SuccessResponse<PetGroups>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
  }),
});

export const {
  useUpdatePetProfileMutation,
  useCreatePetProfileMutation,
  useUpdateProfileMutation,
  useDeletePetProfileMutation,
  useGetPetProfilesMutation,
  useGetPetSpeciesMutation,
  useCreatePetRecommendationsMutation,
  useGetRecommendedProductsMutation,
  useLazyGetCupsPerDayQuery,
  useGetPetBreedTypesQuery,
  useGetPetBreedDetailsQuery,
  useAddBreedMutation,
  useDeleteBreedMutation,
  useLazyGetPetGroupsByCustomerIdQuery,
} = petProfileApi;
